import { useId, useMemo } from 'react';
import { Button } from '../../../components/Button';
import { SelectPopover } from '../../../components/SelectPopover';
import { TextInput } from '../../../components/TextInput';
import type {
  RosterUploadOrganization,
  RosterUploadSourceType,
  RosterUploadStatusFilter,
} from '../../../types/adminRosterUploads';
import {
  SOURCE_FILTER_LABEL,
  STATUS_FILTER_LABEL,
  buildOrganizationOptions,
} from './rosterUploadFormat';
import {
  SOURCE_FILTERS,
  STATUS_FILTERS,
  type RosterUploadsFilters as Filters,
} from './rosterUploadsNavigation';

interface RosterUploadsFiltersProps {
  filters: Filters;
  searchInput: string;
  organizations: readonly RosterUploadOrganization[];
  organizationsFailed: boolean;
  /**
   * A name for a filtered organization that is not in the options (the list has not loaded, or it
   * failed), so the select never claims "All organizations" while the table is filtered.
   */
  selectedOrganizationFallback: string | null;
  onSearchInputChange: (value: string) => void;
  onFiltersChange: (changes: Partial<Filters>) => void;
  onClear: () => void;
}

const STATUS_LABELS = STATUS_FILTERS.map((status) => STATUS_FILTER_LABEL[status]);
const SOURCE_LABELS = SOURCE_FILTERS.map((source) => SOURCE_FILTER_LABEL[source]);

const statusFromLabel = (label: string): RosterUploadStatusFilter | '' =>
  STATUS_FILTERS.find((status) => STATUS_FILTER_LABEL[status] === label) ?? '';

const sourceFromLabel = (label: string): RosterUploadSourceType | '' =>
  SOURCE_FILTERS.find((source) => SOURCE_FILTER_LABEL[source] === label) ?? '';

export const RosterUploadsFilters = ({
  filters,
  searchInput,
  organizations,
  organizationsFailed,
  selectedOrganizationFallback,
  onSearchInputChange,
  onFiltersChange,
  onClear,
}: RosterUploadsFiltersProps) => {
  const groupLabelId = useId();
  const orgSelectId = useId();
  const orgHintId = useId();
  const statusSelectId = useId();
  const sourceSelectId = useId();

  const organizationOptions = useMemo(() => {
    const options = buildOrganizationOptions(organizations);
    if (
      filters.organizationId &&
      !options.some((option) => option.id === filters.organizationId)
    ) {
      options.unshift({
        id: filters.organizationId,
        label: selectedOrganizationFallback ?? 'Selected organization',
      });
    }
    return options;
  }, [organizations, filters.organizationId, selectedOrganizationFallback]);

  const selectedOrganizationLabel =
    organizationOptions.find((option) => option.id === filters.organizationId)?.label ?? '';

  const hasFilters = Boolean(
    filters.organizationId || filters.status || filters.sourceType || searchInput.trim()
  );

  return (
    <div className="roster-uploads__filters" role="group" aria-labelledby={groupLabelId}>
      <span className="roster-uploads__filters-label" id={groupLabelId}>
        Filter uploads
      </span>

      <div className="roster-uploads__field">
        <label className="roster-uploads__field-label" htmlFor={orgSelectId}>
          Organization
        </label>
        <SelectPopover
          id={orgSelectId}
          testId="roster-uploads-org-filter"
          placeholder="All organizations"
          options={organizationOptions.map((option) => option.label)}
          value={selectedOrganizationLabel}
          describedById={organizationsFailed ? orgHintId : undefined}
          onChange={(label) =>
            onFiltersChange({
              organizationId: organizationOptions.find((option) => option.label === label)?.id ?? '',
              page: 1,
            })
          }
        />
        {organizationsFailed && (
          <span className="roster-uploads__field-hint" id={orgHintId}>
            Could not load the organization list.
          </span>
        )}
      </div>

      <div className="roster-uploads__field">
        <label className="roster-uploads__field-label" htmlFor={statusSelectId}>
          Status
        </label>
        <SelectPopover
          id={statusSelectId}
          testId="roster-uploads-status-filter"
          placeholder="All statuses"
          options={STATUS_LABELS}
          value={filters.status ? STATUS_FILTER_LABEL[filters.status] : ''}
          onChange={(label) => onFiltersChange({ status: statusFromLabel(label), page: 1 })}
        />
      </div>

      <div className="roster-uploads__field">
        <label className="roster-uploads__field-label" htmlFor={sourceSelectId}>
          Source
        </label>
        <SelectPopover
          id={sourceSelectId}
          testId="roster-uploads-source-filter"
          placeholder="All sources"
          options={SOURCE_LABELS}
          value={filters.sourceType ? SOURCE_FILTER_LABEL[filters.sourceType] : ''}
          onChange={(label) => onFiltersChange({ sourceType: sourceFromLabel(label), page: 1 })}
        />
      </div>

      <TextInput
        className="roster-uploads__search"
        label="Search"
        type="search"
        placeholder="Organization or uploader"
        value={searchInput}
        onChange={(event) => onSearchInputChange(event.target.value)}
        data-testid="roster-uploads-search"
      />

      {hasFilters && (
        <Button
          type="button"
          variant="secondary"
          className="roster-uploads__clear"
          onClick={onClear}
          data-testid="roster-uploads-clear"
        >
          Clear filters
        </Button>
      )}
    </div>
  );
};
